// CARO. Casa de estilo — money utilities.
// RULE: all monetary values are integer cents. Never use floats for money.

export const CENTS_PER_UNIT = 100;

/** Round half-up to the nearest integer cent (defensive against float drift). */
export function roundCents(value: number): number {
  return Math.round(value + Number.EPSILON);
}

export function addCents(...amounts: number[]): number {
  return amounts.reduce((sum, a) => sum + a, 0);
}

export function multiplyCents(unitCents: number, quantity: number): number {
  if (!Number.isInteger(quantity) || quantity < 0) {
    throw new Error(`Invalid quantity: ${quantity}`);
  }
  return roundCents(unitCents * quantity);
}

/**
 * Format integer cents for display.
 * Defaults to Spanish (es-ES) / EUR at launch; locale/currency are overridable
 * for the EN/FR expansion without changing call sites.
 */
export function formatMoney(
  cents: number,
  currency = 'EUR',
  locale = 'es-ES',
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(cents / CENTS_PER_UNIT);
}

/** Parse a decimal string/number (e.g. "35.90") into integer cents. */
export function toCents(amount: string | number): number {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(n)) throw new Error(`Invalid amount: ${amount}`);
  return roundCents(n * CENTS_PER_UNIT);
}

/** SumUp and most payment APIs expect a major-unit decimal string. */
export function centsToDecimalString(cents: number): string {
  return (cents / CENTS_PER_UNIT).toFixed(2);
}

/**
 * A euros amount as typed in /admin («45», «45.5», «45,50») → integer cents,
 * or null when blank, negative or not a number. Pure.
 */
export function eurosToCents(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return null;
  return toCents(n);
}
